import { useState, useCallback } from 'react';
import { api } from '../helpers/apiCore';
import { endpoints } from '../helpers/Endpoints';
import moment from 'moment';
import axios from 'axios';

interface ExportConfig {
  endpoint: string;
  baseFilename: string;
  timeout?: number;
  additionalParams?: Record<string, string>;
}

interface ExportOptions {
  format: string;
  searchQuery?: string;
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
  filters?: Record<string, string>;
  page?: number;
  perPage?: number;
}

interface UseExportReturn {
  isExporting: boolean;
  exportData: (options: ExportOptions) => Promise<void>;
  exportError: string | null;
  clearError: () => void;
}

const EXPORT_FORMATS = {
  pdf: { 
    contentType: 'application/pdf',
    extension: 'pdf'
  },
  xlsx: { 
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx'
  },
  csv: { 
    contentType: 'text/csv',
    extension: 'csv'
  },
  json: {
    contentType: 'application/json',
    extension: 'json'
  }
} as const;

class ExportUtils {
  static validateFormat(format: string): boolean {
    return format in EXPORT_FORMATS;
  }

  static getFormatConfig(format: string) {
    return EXPORT_FORMATS[format as keyof typeof EXPORT_FORMATS];
  }

  static buildParams(options: ExportOptions, additionalParams: Record<string, string> = {}): Record<string, string> {
    const params: Record<string, string> = {
      format: options.format,
      ...additionalParams,
    };

    if (options.searchQuery?.trim()) {
      params.searchQuery = options.searchQuery.trim();
    }

    if (options.dateRange?.startDate) {
      params.startDate = options.dateRange.startDate;
    }
    if (options.dateRange?.endDate) {
      params.endDate = options.dateRange.endDate;
    }

    if (options.page) {
      params.page = options.page.toString();
    }
    if (options.perPage) {
      params.per_page = options.perPage.toString();
    }

    if (options.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value?.trim()) {
          params[key] = value.trim();
        }
      });
    }

    return params;
  }

  static generateFilename(baseFilename: string, format: string, searchQuery?: string): string {
    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
    const config = this.getFormatConfig(format);
    
    let filename = `${baseFilename}-${timestamp}`;
    
    if (searchQuery?.trim()) {
      filename += '-filtered';
    }
    
    return `${filename}.${config.extension}`;
  }

  static async downloadBlob(blob: Blob, filename: string): Promise<void> {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  static getErrorMessage(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'Export failed. Please try again.';
  }
}

export const useExport = (config: ExportConfig): UseExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportData = useCallback(async (options: ExportOptions) => {
    if (!ExportUtils.validateFormat(options.format)) {
      setExportError(`Unsupported export format: ${options.format}`);
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      const token = api.getLoggedInUser();
      api.setAuthorization(token);

      const params = ExportUtils.buildParams(options, config.additionalParams);

      const response = await axios.get(config.endpoint, {
        params,
        responseType: 'blob',
        timeout: config.timeout || 60000,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const formatConfig = ExportUtils.getFormatConfig(options.format);

      const blob = new Blob([response.data], { type: formatConfig.contentType });

      const filename = ExportUtils.generateFilename(
        config.baseFilename,
        options.format,
        options.searchQuery
      );

      await ExportUtils.downloadBlob(blob, filename);

    } catch (error: any) {
      const errorMessage = ExportUtils.getErrorMessage(error);
      setExportError(errorMessage);
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  }, [config]);


  const clearError = useCallback(() => {
    setExportError(null);
  }, []);

  return {
    isExporting,
    exportData,
    exportError,
    clearError
  };
};

export const EXPORT_CONFIGS = {
  schoolPayments: {
    endpoint: endpoints.downloadSchoolPayments,
    baseFilename: 'school-payments',
    timeout: 60000,
  },
  transactions: {
    endpoint: endpoints.downloadTransactions,
    baseFilename: 'transactions',
    timeout: 60000,
  },
} as const;

export const EXPORT_FORMAT_OPTIONS = [
  {
    format: 'csv',
    label: 'CSV',
    description: 'Comma-separated values',
    icon: '📊'
  },
  {
    format: 'xlsx',
    label: 'Excel',
    description: 'Microsoft Excel format',
    icon: '📈'
  },
  {
    format: 'pdf',
    label: 'PDF',
    description: 'Portable Document Format',
    icon: '📄'
  },
  {
    format: 'json',
    label: 'JSON',
    description: 'JavaScript Object Notation',
    icon: '🔧'
  }
] as const;

export default useExport;